import React, { forwardRef, type ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'ghost' | 'primary' | 'pill' | 'danger' | 'surface';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render as a pill with icon + label */
  icon?: React.ReactNode;
  /** Active state (for toggle buttons like builder modes) */
  active?: boolean;
}

const variantStyles: Record<ButtonVariant, { base: string; active: string }> = {
  ghost: {
    base: 'bg-transparent hover:bg-[var(--sage-white-5)] text-[var(--sage-text-muted)] hover:text-[var(--sage-text)]',
    active: 'bg-[var(--sage-white-10)] text-[var(--sage-text)]',
  },
  primary: {
    base: 'bg-[var(--sage-primary)] hover:bg-[var(--sage-primary-hover)] text-white shadow-lg',
    active: 'bg-[var(--sage-primary-hover)] text-white',
  },
  pill: {
    base: 'bg-[var(--sage-surface)] border border-[var(--sage-white-5)] hover:border-[color-mix(in_srgb,var(--sage-primary)_30%,transparent)] text-[var(--sage-text)] hover:text-white',
    active: 'bg-[var(--sage-primary)] text-white border-[var(--sage-primary)]',
  },
  danger: {
    base: 'bg-transparent hover:bg-[color-mix(in_srgb,var(--sage-danger)_15%,transparent)] text-[var(--sage-danger)]',
    active: 'bg-[var(--sage-danger)] text-white',
  },
  surface: {
    base: 'bg-[var(--sage-surface)] hover:bg-[var(--sage-surface-hover)] text-[var(--sage-text)] border border-[var(--sage-white-5)]',
    active: 'bg-[var(--sage-surface-hover)] text-white',
  },
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-2 py-1 text-[11px] gap-1',
  md: 'px-3 py-1.5 text-[13px] gap-1.5',
  lg: 'px-4 py-2.5 text-sm gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'ghost', size = 'md', icon, active, className = '', children, disabled, ...rest }, ref) => {
    const vs = variantStyles[variant];
    const ss = sizeStyles[size];
    const stateStyle = active ? vs.active : vs.base;

    return (
      <button
        ref={ref}
        className={[
          'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 cursor-pointer select-none',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          variant === 'pill' ? 'rounded-full' : '',
          ss,
          stateStyle,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        {...rest}
      >
        {icon}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
